import Box from '@mui/material/Box';
import { noop } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { ReactNode, useCallback, useEffect, useState } from 'react';

import { FeatureFlags } from '~constants/featureFlags';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import CategoryApprovalsCard from '~pages/Approvals/DriverDay/components/CategoryApprovalsCard';
import JobDetailsCard, {
  JobDetailsTabs,
} from '~pages/Approvals/DriverDay/components/JobDetails';
import { useStores } from '~store';
import { spacing } from '~theme/AppTheme';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import JobDetailsRedesigned from './JobDetailsRedesigned';
import JobTimesheetsCard from './JobTimesheetsCard';

interface JobDetailsProps {
  belowApprovals?: ReactNode;
  belowTimesheets?: ReactNode;
  columnWidths?: string;
  includeTimelineInJobDetailsTab?: boolean;
  job: Job;
  onJobUpdated?: (job: Job) => void;
}

const JobDetails = observer(
  ({
    belowApprovals,
    belowTimesheets,
    columnWidths,
    includeTimelineInJobDetailsTab,
    job: initialJob,
    onJobUpdated,
  }: JobDetailsProps) => {
    const { userStore, approveJobsStore } = useStores();
    const companyId = getEffectiveUserCompanyId(userStore);
    // An unregistered Split treatment resolves to false, so this defaults OFF
    // and production keeps today's layout until the flag is turned on.
    const approveJobsRedesign = useFeatureFlag({
      featureFlagKey: FeatureFlags.approveJobsRedesign,
    });
    const { getJobById } = useJob();

    const [job, setJob] = useState(initialJob);

    const handleJobUpdated = useCallback(
      (job: Job) => {
        setJob(job);
        approveJobsStore.upsertJob(job);

        onJobUpdated?.(job);
      },
      [onJobUpdated],
    );

    const handleReloadJob = useCallback(async () => {
      approveJobsStore.updateJobStart(job.id);

      const updatedJob = await getJobById(job.id, companyId);

      if (updatedJob) {
        setJob(updatedJob);
        approveJobsStore.updateJobSuccess(updatedJob);
        onJobUpdated?.(updatedJob);
      }
    }, [job.id, companyId, onJobUpdated]);

    useEffect(() => {
      setJob(initialJob);
    }, [initialJob]);

    const detailContent = (
      <Box
        data-test-id="job-details-layout"
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: columnWidths ?? '60% 40%',
          maxWidth: `calc(100% - ${spacing(2)})`,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <JobTimesheetsCard job={job} onJobUpdated={handleJobUpdated} />
          {belowTimesheets}

          <JobDetailsCard
            editable={job.editable}
            hideApprovalCTA
            hideJobSummaryCard
            hideMapToggle
            job={job}
            onJobUpdated={handleJobUpdated}
            onReloadJob={handleReloadJob}
            onToggleJob={noop}
            toggledJob={null}
          />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <CategoryApprovalsCard
            billingApprovedAt={job?.billingApprovedAt}
            fieldApprovedAt={job?.fieldApprovedAt}
            jobId={job.id}
          />
          {belowApprovals}

          <JobDetailsTabs
            toggledJob={job}
            showTimeline={includeTimelineInJobDetailsTab}
          />
        </Box>
      </Box>
    );

    if (approveJobsRedesign) {
      // Redesigned Approve Jobs detail (hero + inline Rec/Pay + live notes).
      // Remaining stacked PRs layer on:
      //   TODO(TRE-16330, https://linear.app/treadapp/issue/TRE-16330): tab
      //     consolidation (handled in ExpandedBlock)
      //   TODO(TRE-16331, https://linear.app/treadapp/issue/TRE-16331): map + hours
      //     workspace launched from the hero
      return (
        <JobDetailsRedesigned
          belowApprovals={belowApprovals}
          belowTimesheets={belowTimesheets}
          columnWidths={columnWidths}
          includeTimelineInJobDetailsTab={includeTimelineInJobDetailsTab}
          job={initialJob}
          onJobUpdated={onJobUpdated}
        />
      );
    }

    return detailContent;
  },
);

export default JobDetails;
